import { createFileRoute, Link } from "@tanstack/react-router";
import { Reveal } from "@/components/common/reveal";
import { siteConfig } from "@/config/site";
import { FileText, ShieldAlert, Truck, RotateCcw } from "lucide-react";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: `Terms of Service — ${siteConfig.name}` },
      {
        name: "description",
        content:
          "Terms of Service for Aster Decorz. Read our commercial terms, prepaid payment rules, order processing, shipping, and 6-day return policies across India.",
      },
      { property: "og:title", content: `Terms of Service — ${siteConfig.shortName}` },
      {
        property: "og:description",
        content:
          "Commercial terms, payment policies, and order fulfillment conditions for Aster Decorz.",
      },
      { property: "og:url", content: `${siteConfig.url}/terms` },
    ],
    links: [{ rel: "canonical", href: `${siteConfig.url}/terms` }],
  }),
  component: TermsOfService,
});

function TermsOfService() {
  const lastUpdated = "October 19, 2026";

  return (
    <div className="pb-28 pt-24 md:pt-28">
      <div className="section-container max-w-4xl">
        <Reveal>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-800">
            <FileText className="h-4 w-4" />
            <span>Store Agreement</span>
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl">Terms of Service</h1>
          <p className="mt-2 text-xs text-muted-foreground">
            Effective Date: {lastUpdated} | Aster Decorz (asterdecorz.com)
          </p>
        </Reveal>

        <div className="mt-12 space-y-10 text-sm leading-relaxed text-muted-foreground border-t pt-10">
          <section>
            <h2 className="font-display text-xl text-foreground mb-3">1. Agreement to Terms</h2>
            <p>
              By accessing or making a purchase on{" "}
              <Link to="/" className="text-primary underline">
                asterdecorz.com
              </Link>
              , you agree to be bound by these Terms of Service, all applicable laws and regulations
              in India, and agree that you are responsible for compliance with any local laws. If
              you do not agree with any of these terms, you are prohibited from using or accessing
              this site.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">
              2. Products, Accuracy & Handmade Variations
            </h2>
            <p className="mb-2">
              Aster Decorz specializes in premium handcrafted artificial plants, faux bonsai trees,
              hanging floral creepers, and biophilic botanical decor:
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                Each botanical piece is assembled by hand using real reclaimed wood trunks and
                hand-dyed silk foliage. Minor natural variances in trunk contour, branch
                configuration, and leaf arrangement are inherent characteristics of artisan
                craftsmanship.
              </li>
              <li>
                We make every effort to display true-to-life colors and dimensions. However, actual
                perceived colors may vary slightly depending on monitor display calibrations and
                room lighting.
              </li>
              <li>
                All items are subject to stock availability. We reserve the right to discontinue or
                modify items without prior notice.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">
              3. Pricing & 100% Secure Prepaid Payment Policy
            </h2>
            <p className="mb-2">
              All prices displayed on Aster Decorz are denominated in Indian Rupees (INR) and are
              subject to applicable Goods and Services Tax (GST):
            </p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <strong className="text-foreground">Prepaid Store Policy:</strong> Aster Decorz
                operates strictly as a 100% prepaid e-commerce platform. We do not support Cash on
                Delivery (COD).
              </li>
              <li>
                <strong className="text-foreground">Payment Authorization:</strong> Payments are
                processed securely via Razorpay (UPI, Credit/Debit Cards, Net Banking). An order is
                authoritatively confirmed only when our server verifies a valid cryptographic
                payment signature from the payment gateway.
              </li>
              <li>
                In the event of a technical deduction where an order reference was not generated,
                the full deducted amount will be automatically reversed to your original payment
                source within 5–7 banking days.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">
              4. Shipping, Transit & Delivery
            </h2>
            <div className="flex items-start gap-3 p-4 rounded-xl border bg-card mb-4">
              <Truck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-semibold text-foreground mb-1">Standard Delivery Timeline</p>
                <p>
                  Orders are dispatched within 24–48 business hours from our Tamil Nadu workshop.
                  Estimated courier transit time across India is 5 to 7 business days.
                </p>
              </div>
            </div>
            <p>
              Free standard shipping applies on all orders exceeding ₹5,000. For orders under
              ₹5,000, a flat shipping fee of ₹60 is applied at checkout. Express courier options are
              available at custom rates calculated at checkout.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">
              5. Returns, Damages & Replacements
            </h2>
            <div className="flex items-start gap-3 p-4 rounded-xl border bg-card mb-4">
              <RotateCcw className="h-5 w-5 text-emerald-800 shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-semibold text-foreground mb-1">
                  6-Day Return & Replacement Policy
                </p>
                <p>
                  Customers may request a replacement or return within 6 calendar days of confirmed
                  delivery if an item arrives broken, defective, or noticeably different from the
                  ordered specifications.
                </p>
              </div>
            </div>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                To initiate a claim, reach out via WhatsApp (+91 9344580982) or email
                admin@asterdecorz.com with your order number and unboxing photos/video of the
                damage.
              </li>
              <li>
                Items must be returned in their original packaging, including the protective inner
                cushioning and pot enclosure.
              </li>
              <li>
                Approved refunds are credited to the customer&rsquo;s original payment method within
                5–7 business days after the returned item passes quality inspection.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-xl text-foreground mb-3">
              6. Limitation of Liability & Governing Law
            </h2>
            <p>
              Aster Decorz shall not be liable for any indirect, incidental, or consequential
              damages resulting from the use or inability to use our website or products. These
              terms are governed by and construed in accordance with the laws of India. Any legal
              dispute or claim arising under these terms shall be subject to the exclusive
              jurisdiction of the courts in Tamil Nadu, India.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
